/**
 * Where you are in the app.
 *
 * Four destinations, grouped by what someone is trying to do rather than by
 * which epic built them — "what do I own", "what should I do about it", "what
 * does this tell me", "set it up". That grouping is not invented here: the
 * requirements already divide along those lines, and the first version of this
 * UI simply did not reflect them. It stacked every screen into one column with
 * seven peer buttons in the middle, which is a menu rather than navigation.
 *
 * Secondary screens are views *within* a destination, not peers of it. Release
 * and Investments are two ways of acting on a decision; making them siblings of
 * the catalog said they were different kinds of thing.
 *
 * The current view lives in the URL. That is what makes a notification able to
 * open the review screen (`sw.ts` posts `/?view=review`), and what makes the
 * browser's back button mean something.
 */
export type Destination = 'catalog' | 'decide' | 'insight' | 'settings';

export type View =
  | 'catalog'
  | 'review'
  | 'release'
  | 'invest'
  | 'reports'
  | 'export'
  | 'account'
  | 'appearance'
  | 'reminders'
  | 'organize'
  | 'diagnostics';

interface ViewMeta {
  readonly destination: Destination;
  readonly label: string;
}

/**
 * Every view, and the destination it belongs to.
 *
 * One table rather than a switch per question, so a view cannot be routed to one
 * destination and labeled under another.
 */
export const VIEWS: Readonly<Record<View, ViewMeta>> = {
  catalog: { destination: 'catalog', label: 'Catalog' },

  review: { destination: 'decide', label: 'Review' },
  release: { destination: 'decide', label: 'Release' },
  invest: { destination: 'decide', label: 'Investments' },

  reports: { destination: 'insight', label: 'Reports' },
  export: { destination: 'insight', label: 'Export' },

  account: { destination: 'settings', label: 'Account' },
  appearance: { destination: 'settings', label: 'Appearance' },
  reminders: { destination: 'settings', label: 'Reminders' },
  organize: { destination: 'settings', label: 'Organize' },
  diagnostics: { destination: 'settings', label: 'Diagnostics' },
};

export const DESTINATIONS: readonly { id: Destination; label: string; intent: string }[] = [
  { id: 'catalog', label: 'Catalog', intent: 'what you own' },
  { id: 'decide', label: 'Decide', intent: 'what to do about it' },
  { id: 'insight', label: 'Insight', intent: 'what it tells you' },
  { id: 'settings', label: 'Settings', intent: 'how it is set up' },
];

/** The view a destination opens on. */
export const LANDING: Readonly<Record<Destination, View>> = {
  catalog: 'catalog',
  decide: 'review',
  insight: 'reports',
  settings: 'account',
};

/** The views within a destination, in the order they are offered. */
export const viewsIn = (destination: Destination): View[] =>
  (Object.keys(VIEWS) as View[]).filter((view) => VIEWS[view].destination === destination);

export const isView = (value: string | null): value is View =>
  value !== null && Object.prototype.hasOwnProperty.call(VIEWS, value);

/**
 * The view named by a URL, or the catalog.
 *
 * An unknown view falls back rather than erroring. The value can come from a
 * stale notification, a bookmark made before a rename, or someone editing the
 * address bar, and none of those deserves a broken page.
 */
export function viewFromUrl(search: string): View {
  const asked = new URLSearchParams(search).get('view');
  return isView(asked) ? asked : 'catalog';
}

/** The query string for a view, with `catalog` left bare as the default. */
export const urlFor = (view: View): string => (view === 'catalog' ? '/' : `/?view=${view}`);
